import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { OverlayContainer } from "@angular/cdk/overlay";
import { initializeDynamicTheme, clearDynamicTheme, updateDynamicTheme } from "./dynamic-theme-loader";

export enum NexuTheme {
  LEGACY = "legacy",
  DEFAULT = "rebrand-blue-green",
  DARK = "dark",
}

/**
 * Service for managing the application theme
 * Uses CSS classes to apply different Angular Material themes
 * Also supports dynamic color themes from session context
 */
@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private currentTheme: NexuTheme = NexuTheme.DEFAULT;
  private readonly themeClass = {
    [NexuTheme.LEGACY]: "legacy",
    [NexuTheme.DEFAULT]: "",
    [NexuTheme.DARK]: "dark-theme",
  };

  /** Emits current dynamic theme colors coming from localStorage session_context */
  private readonly dynamicColorsSubject = new BehaviorSubject<{
    neutral: string;
    primary: string;
    secondary: string;
  } | null>(null);
  readonly dynamicColors$ = this.dynamicColorsSubject.asObservable();

  constructor(private readonly overlayContainer: OverlayContainer) {}

  /**
   * Initialize the theme service
   * Can be called in app initialization to set the initial theme
   * Note: Dynamic theme from session_context should be initialized
   * before Angular bootstraps for best performance
   */
  initialize(initialTheme?: NexuTheme): void {
    // Initialize dynamic theme if not already done
    // (This is safe to call multiple times)
    initializeDynamicTheme();

    // Check for saved theme preference in localStorage
    const savedTheme = localStorage.getItem("nexu-theme");
    if (savedTheme && Object.values(NexuTheme).includes(savedTheme as NexuTheme)) {
      this.setTheme(savedTheme as NexuTheme);
    } else if (initialTheme) {
      this.setTheme(initialTheme);
    } else {
      // Check for system preference if no saved theme
      this.applySystemPreference();
    }
  }

  /**
   * Check system preference for dark mode
   */
  private applySystemPreference(): void {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      this.setTheme(NexuTheme.DARK);
    }

    // Listen for changes in system preference
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", (e) => {
      const newTheme = e.matches ? NexuTheme.DARK : NexuTheme.DEFAULT;
      this.setTheme(newTheme);
    });
  }

  /**
   * Set the active theme
   * @param theme The theme to apply
   */
  setTheme(theme: NexuTheme): void {
    // Remove current theme class if any
    if (this.currentTheme !== NexuTheme.DEFAULT) {
      document.body.classList.remove(this.themeClass[this.currentTheme]);
      // Mirror removal on overlay container for dialogs/menus, etc.
      this.overlayContainer.getContainerElement().classList.remove(this.themeClass[this.currentTheme]);
    }

    // Add new theme class if not default
    if (theme !== NexuTheme.DEFAULT && this.themeClass[theme]) {
      document.body.classList.add(this.themeClass[theme]);
      // Mirror add on overlay container for dialogs/menus, etc.
      this.overlayContainer.getContainerElement().classList.add(this.themeClass[theme]);
    }

    this.currentTheme = theme;

    // Save theme preference to localStorage
    localStorage.setItem("nexu-theme", theme);
  }

  /**
   * Get the current active theme
   */
  getCurrentTheme(): NexuTheme {
    return this.currentTheme;
  }

  /**
   * Toggle between light and dark themes only
   */
  toggleDarkMode(): void {
    const newTheme = this.currentTheme === NexuTheme.DARK ? NexuTheme.DEFAULT : NexuTheme.DARK;
    this.setTheme(newTheme);
  }

  /**
   * Toggle between all themes
   */
  toggleTheme(): void {
    switch (this.currentTheme) {
      case NexuTheme.DEFAULT:
        this.setTheme(NexuTheme.LEGACY);
        break;
      case NexuTheme.LEGACY:
        this.setTheme(NexuTheme.DARK);
        break;
      case NexuTheme.DARK:
      default:
        this.setTheme(NexuTheme.DEFAULT);
        break;
    }
  }

  /**
   * Check if dark mode is currently active
   */
  isDarkMode(): boolean {
    return this.currentTheme === NexuTheme.DARK;
  }

  /**
   * Update dynamic theme colors at runtime
   * @param theme Object containing neutral, primary, and secondary colors
   */
  updateDynamicColors(theme: { neutral: string; primary: string; secondary: string }): void {
    updateDynamicTheme(theme);
  }

  /**
   * Clear dynamic theme and restore defaults
   */
  clearDynamicColors(): void {
    clearDynamicTheme();
    this.dynamicColorsSubject.next(null);
  }

  /**
   * Reinitialize dynamic theme from session context
   * Useful after updating localStorage manually
   */
  reloadDynamicTheme(): void {
    initializeDynamicTheme();
    this.emitColorsFromLocalStorage();
  }

  /**
   * Apply dynamic theme from localStorage `session_context` now.
   * Initializes Material palettes and CSS variables and syncs overlay container.
   */
  applyTheme(): void {
    initializeDynamicTheme();
    this.emitColorsFromLocalStorage();
    // Ensure overlay container carries the same theme class as body
    const bodyHasLegacy = document.body.classList.contains(this.themeClass[NexuTheme.LEGACY]);
    const bodyHasDark = document.body.classList.contains(this.themeClass[NexuTheme.DARK]);
    const overlayEl = this.overlayContainer.getContainerElement();
    overlayEl.classList.toggle(this.themeClass[NexuTheme.LEGACY], bodyHasLegacy);
    overlayEl.classList.toggle(this.themeClass[NexuTheme.DARK], bodyHasDark);
  }

  /** Update only the primary color and re-apply dynamic theme. */
  updatePrimaryColor(hex: string): void {
    const colors = this.getColorsFromLocalStorage();
    updateDynamicTheme({
      neutral: colors?.neutral ?? "#2B2B2B",
      primary: hex,
      secondary: colors?.secondary ?? "#666666",
    });
    this.emitColorsFromLocalStorage();
  }

  /** Update only the accent/secondary color and re-apply dynamic theme. */
  updateAccentColor(hex: string): void {
    const colors = this.getColorsFromLocalStorage();
    updateDynamicTheme({
      neutral: colors?.neutral ?? "#2B2B2B",
      primary: colors?.primary ?? "#EC0000",
      secondary: hex,
    });
    this.emitColorsFromLocalStorage();
  }

  /** Update only the neutral/base color and re-apply dynamic theme. */
  updateNeutralColor(hex: string): void {
    const colors = this.getColorsFromLocalStorage();
    updateDynamicTheme({
      neutral: hex,
      primary: colors?.primary ?? "#EC0000",
      secondary: colors?.secondary ?? "#666666",
    });
    this.emitColorsFromLocalStorage();
  }

  private emitColorsFromLocalStorage(): void {
    try {
      const ctx = localStorage.getItem("session_context");
      if (!ctx) {
        this.dynamicColorsSubject.next(null);
        return;
      }
      const parsed = JSON.parse(ctx);
      const theme = parsed?.theme;
      if (
        theme &&
        typeof theme.neutral === "string" &&
        typeof theme.primary === "string" &&
        typeof theme.secondary === "string"
      ) {
        this.dynamicColorsSubject.next(theme);
      } else {
        this.dynamicColorsSubject.next(null);
      }
    } catch {
      this.dynamicColorsSubject.next(null);
    }
  }

  private getColorsFromLocalStorage(): { neutral: string; primary: string; secondary: string } | null {
    try {
      const ctx = localStorage.getItem("session_context");
      if (!ctx) return null;
      const parsed = JSON.parse(ctx);
      return parsed?.theme ?? null;
    } catch {
      return null;
    }
  }
}
